import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import './LoginForm.css';

export interface LoginFormProps {
  /**
   * Handler fired with the raw (trimmed) login identifier — a phone number OR
   * an email address (#2008c). The component does NOT normalize; the consumer
   * owns channel detection + normalization.
   */
  onOtpRequest?: (identifier: string) => void;
  /** Handler for terms consent error */
  onTermsError?: () => void;
  /** Placeholder text for the identifier input (phone or email). */
  identifierPlaceholder?: string;
  /**
   * @deprecated Use {@link identifierPlaceholder}. Kept as a back-compat alias
   * so existing callers keep working while the field widens to phone-OR-email.
   */
  phonePlaceholder?: string;
  /** Show terms and conditions */
  showTerms?: boolean;
  /** Terms of service URL */
  termsUrl?: string;
  /** Privacy policy URL */
  privacyUrl?: string;
  /** Terms intro text */
  termsIntro?: string;
  /** Terms of service link text */
  termsText?: string;
  /** Privacy policy link text */
  privacyText?: string;
  /** Custom className */
  className?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Initial identifier (phone or email) to prefill (e.g. last login on this device). */
  initialIdentifier?: string;
  /**
   * @deprecated Use {@link initialIdentifier}. Back-compat alias.
   */
  initialPhone?: string;
}

/**
 * A value looks like an email once it carries an ``@`` or any letter — a bare
 * phone number is only digits, spaces and ``+``. This drives the dynamic
 * ``inputMode`` so a mobile keyboard shows the numeric pad for phones and the
 * email keyboard (with ``@``) once the user starts typing an address.
 */
function looksLikeEmail(value: string): boolean {
  return /[a-zA-Z@]/.test(value);
}

/**
 * LoginForm component provides OTP authentication over a single identifier
 * field that accepts a phone number (SMS) OR an email address (#2008c).
 */
export const LoginForm: React.FC<LoginFormProps> = ({
  onOtpRequest,
  onTermsError,
  identifierPlaceholder,
  phonePlaceholder,
  showTerms = true,
  termsUrl = '#',
  privacyUrl = '#',
  termsIntro = 'By continuing, you agree to our',
  termsText = 'Terms of Service',
  privacyText = 'Privacy Policy',
  className = '',
  isLoading = false,
  initialIdentifier,
  initialPhone,
}) => {
  const placeholder = identifierPlaceholder ?? phonePlaceholder ?? 'Phone number';
  const [identifier, setIdentifier] = useState(initialIdentifier ?? initialPhone ?? '');
  const [termsAccepted, setTermsAccepted] = useState(true);

  const inputMode: 'tel' | 'email' = looksLikeEmail(identifier) ? 'email' : 'tel';

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      onTermsError?.();
      return;
    }
    if (onOtpRequest && identifier.trim() && !isLoading) {
      onOtpRequest(identifier.trim());
    }
  };

  const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIdentifier(e.target.value);
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(e.target.checked);
  };

  return (
    <div className={`login-form ${className}`}>
      <div className="login-form-content">
        {/* Identifier (phone OR email) OTP Form */}
        <form onSubmit={handleOtpSubmit} className="login-form-phone-form">
          <div className="login-form-input-wrapper">
            <input
              type="text"
              inputMode={inputMode}
              autoComplete="username"
              placeholder={placeholder}
              value={identifier}
              onChange={handleIdentifierChange}
              className="login-form-input"
              disabled={isLoading}
              aria-label="Phone number"
            />
            <button
              type="submit"
              className="login-form-submit-button"
              disabled={!identifier.trim() || isLoading}
              aria-label="Submit phone number"
            >
              <ChevronRight className="login-form-submit-icon" />
            </button>
          </div>
        </form>

        {/* Terms and Conditions with Checkbox */}
        {showTerms && (
          <label className="login-form-terms-label">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={handleTermsChange}
              className="login-form-terms-checkbox"
            />
            <span className="login-form-terms-text">
              {termsIntro}{' '}
              <a href={termsUrl} className="login-form-link" onClick={(e) => e.stopPropagation()}>
                {termsText}
              </a>{' '}
              &amp;{' '}
              <a href={privacyUrl} className="login-form-link" onClick={(e) => e.stopPropagation()}>
                {privacyText}
              </a>
              .
            </span>
          </label>
        )}
      </div>
    </div>
  );
};
